import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { TeamDto } from '../../web-api-client';

interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newPicId: string, reason?: string) => Promise<void>;
  currentPicId?: string;
  currentPicName?: string;
  taskName: string;
}

export default function ReassignModal({
  isOpen,
  onClose,
  onSubmit,
  currentPicId,
  currentPicName,
  taskName
}: ReassignModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchTeams();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await usersService.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const data = await teamsService.getTeams();
      setTeams(data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;

    setSubmitting(true);
    try {
      // Extract the actual ID (remove team- or person- prefix)
      let picId = selectedUserId;
      if (selectedUserId.startsWith('team-')) {
        picId = selectedUserId.replace('team-', '');
      } else if (selectedUserId.startsWith('person-')) {
        picId = selectedUserId.replace('person-', '');
      }

      await onSubmit(picId, reason.trim() || undefined);
      setSelectedUserId('');
      setReason('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <UserIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Reassign Task</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">Task: {taskName}</p>

        {currentPicName && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-900">
              <strong>Currently Assigned:</strong> {currentPicName}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="newPic" className="block text-sm font-medium text-gray-700 mb-1">
              Assign To *
            </label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading...</div>
            ) : (
              <select
                id="newPic"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select assignment...</option>
                <optgroup label="─── Teams ───">
                  {teams.map((team) => (
                    <option key={`team-${team.id}`} value={`team-${team.id}`}>
                      👥 {team.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="─── Persons ───">
                  {users
                    .filter(u => u.id !== currentPicId)
                    .map((user) => (
                      <option key={`person-${user.id}`} value={`person-${user.id}`}>
                        👤 {user.userName} ({user.email})
                      </option>
                    ))}
                </optgroup>
              </select>
            )}
          </div>

          <div>
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
              Reason (Optional)
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this task being reassigned?"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedUserId}
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Reassigning...' : 'Reassign'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
